import { Skeleton } from '@/components/ui/skeleton';
import type { TConsumer } from '@/services/consumer';
import { parseDateToDMY } from '@/utils/utils';
import { useTranslation } from 'react-i18next';

type TUserInfo = {
  user?: TConsumer;
};

type UserInfoFieldProps = {
  label: string;
  value: React.ReactNode;
};

const UserInfoField = ({ label, value }: UserInfoFieldProps) => (
  <div>
    <h4 className="text-sm font-medium text-gray-500">{label}</h4>
    <p>{value}</p>
  </div>
);

export const UserInfo = ({ user }: TUserInfo) => {
  const { t } = useTranslation();

  if (!user) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 9 }).map((_, index) => (
          <div key={index} className="flex flex-col gap-2">
            <Skeleton className="w-20 h-4" />
            <Skeleton className="w-40 h-4" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      <UserInfoField
        label={t('clients.sections.userInfo.firstName')}
        value={user.firstName}
      />
      <UserInfoField
        label={t('clients.sections.userInfo.lastName')}
        value={user.lastName}
      />
      <UserInfoField
        label={t('clients.sections.userInfo.email')}
        value={user.email ?? '-'}
      />
      <UserInfoField
        label={t('clients.sections.userInfo.phone')}
        value={user.phone ?? '-'}
      />
      <UserInfoField
        label={t('clients.sections.userInfo.identification')}
        value={user.identification?.number ?? '-'}
      />
      <UserInfoField
        label={t('clients.sections.userInfo.birthdate')}
        value={parseDateToDMY(user.birthdate)}
      />
      <UserInfoField
        label={t('clients.sections.userInfo.weight')}
        value={user.weight ? `${user.weight} kg` : '-'}
      />
      <UserInfoField
        label={t('clients.sections.userInfo.height')}
        value={user.height ? `${user.height} cm` : '-'}
      />
      <UserInfoField
        label={t('clients.sections.userInfo.location')}
        value={user.location ?? '-'}
      />
      <UserInfoField
        label={t('clients.sections.userInfo.address')}
        value={user.address ?? '-'}
      />
      <UserInfoField
        label={t('clients.sections.userInfo.postalCode')}
        value={user.postalCode ?? '-'}
      />
      <UserInfoField
        label={t('clients.sections.userInfo.emergencyContact')}
        value={
          user.emergencyContact.fullName
            ? `${user.emergencyContact.fullName} (${user.emergencyContact.phone})`
            : '-'
        }
      />
      <UserInfoField
        label={t('clients.sections.userInfo.joinedDate')}
        value={parseDateToDMY(user.createdAt)}
      />
    </div>
  );
};
